import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  HR_DEVELOPMENT_PLAN_STATUSES,
  HR_PERFORMANCE_CALIBRATION_STATUSES,
  HR_PERFORMANCE_CYCLE_STATUSES,
  HR_PERFORMANCE_GOAL_STATUSES,
  HR_PERFORMANCE_REVIEW_STATUSES,
  HR_PERFORMANCE_REVIEW_TYPES,
  HrDevelopmentPlan,
  HrEmployee,
  HrPerformanceCycle,
  HrPerformanceDashboard,
  HrPerformanceGoal,
  HrPerformanceKpiContext,
  HrPerformanceReview,
  HrPerformanceReviewRating,
  HrPerformanceReviewSection
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-performance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-performance.component.html',
  styleUrls: ['./hr-performance.component.scss']
})
export class HrPerformanceComponent implements OnInit {
  activeListTab = 'reviews';
  activeDialog?: 'cycle' | 'goal' | 'review' | 'calibration' | 'development';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.performance.manage);
  readonly canReview = this.permissionService.hasPermission(app_permissions.hr.performance.review);
  readonly canCalibrate = this.permissionService.hasPermission(app_permissions.hr.performance.calibrate);
  readonly cycleStatusOptions = HR_PERFORMANCE_CYCLE_STATUSES.map(value => ({ label: value, value }));
  readonly goalStatusOptions = HR_PERFORMANCE_GOAL_STATUSES.map(value => ({ label: value, value }));
  readonly reviewStatusOptions = HR_PERFORMANCE_REVIEW_STATUSES.map(value => ({ label: value, value }));
  readonly reviewTypeOptions = HR_PERFORMANCE_REVIEW_TYPES.map(value => ({ label: value, value }));
  readonly calibrationStatusOptions = HR_PERFORMANCE_CALIBRATION_STATUSES.map(value => ({ label: value, value }));
  readonly developmentPlanStatusOptions = HR_DEVELOPMENT_PLAN_STATUSES.map(value => ({ label: value, value }));
  workerCategoryOptions: SelectOption<string>[] = [];

  dashboard?: HrPerformanceDashboard;
  employees: HrEmployee[] = [];
  cycles: HrPerformanceCycle[] = [];
  goals: HrPerformanceGoal[] = [];
  reviews: HrPerformanceReview[] = [];
  developmentPlans: HrDevelopmentPlan[] = [];
  kpiContext: HrPerformanceKpiContext[] = [];

  cycleForm: HrPerformanceCycle = { cycleStatus: 'DRAFT', calibrationStatus: 'NOT_STARTED' };
  goalForm: HrPerformanceGoal = { goalStatus: 'OPEN' };
  reviewForm: HrPerformanceReview = { reviewType: 'MANAGER', reviewStatus: 'DRAFT', calibrationStatus: 'NOT_CALIBRATED' };
  sectionForm: HrPerformanceReviewSection = { sectionType: 'COMPETENCY' };
  ratingForm: HrPerformanceReviewRating = { ratingType: 'GOAL' };
  developmentPlanForm: HrDevelopmentPlan = { planStatus: 'OPEN' };

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.load();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || item.categoryCode || '', value: item.categoryCode || ''
      }));
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Cycles', value: this.dashboard?.cycles || 0, tone: 'info' },
      { label: 'Active Cycles', value: this.dashboard?.activeCycles || 0, tone: 'success' },
      { label: 'Open Goals', value: this.dashboard?.openGoals || 0, tone: 'warn' },
      { label: 'Submitted Reviews', value: this.dashboard?.submittedReviews || 0, tone: 'info' },
      { label: 'Open Development Plans', value: this.dashboard?.openDevelopmentPlans || 0, tone: 'warn' }
    ];
  }

  get employeeOptions(): SelectOption<number>[] {
    return this.employees.map(employee => ({
      label: `${employee.employeeNumber || employee.employeeId} - ${employee.displayName}`,
      value: employee.employeeId!
    }));
  }

  get cycleOptions(): SelectOption<number>[] {
    return this.cycles.map(cycle => ({ label: `${cycle.cycleCode} - ${cycle.cycleName}`, value: cycle.performanceCycleId! }));
  }

  get goalOptions(): SelectOption<number>[] {
    return this.goals.map(goal => ({ label: `${goal.employeeName || goal.employeeId} - ${goal.goalTitle}`, value: goal.performanceGoalId! }));
  }

  get reviewOptions(): SelectOption<number>[] {
    return this.reviews.map(review => ({
      label: `${review.employeeName || review.employeeId} - ${review.reviewType} - ${review.reviewStatus}`,
      value: review.performanceReviewId!
    }));
  }

  load(): void {
    forkJoin({
      dashboard: this.hrService.performanceDashboard(),
      employees: this.hrService.employees({ page: 0, pageSize: 500 }),
      cycles: this.hrService.performanceCycles(),
      goals: this.hrService.performanceGoals(),
      reviews: this.hrService.performanceReviews(),
      plans: this.hrService.developmentPlans(),
      kpi: this.hrService.performanceKpiContext()
    }).subscribe({
      next: result => {
        this.dashboard = result.dashboard.data;
        this.employees = result.employees.items || [];
        this.cycles = result.cycles.data || [];
        this.goals = result.goals.data || [];
        this.reviews = result.reviews.data || [];
        this.developmentPlans = result.plans.data || [];
        this.kpiContext = result.kpi.data || [];
      },
      error: () => this.toast('error', 'Unable to load performance workspace.')
    });
  }

  saveCycle(): void {
    if (!this.canManage) return;
    this.hrService.savePerformanceCycle(this.cycleForm).subscribe({
      next: () => {
        this.cycleForm = { cycleStatus: 'DRAFT', calibrationStatus: 'NOT_STARTED' };
        this.activeDialog = undefined;
        this.toast('success', 'Performance cycle saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save cycle.')
    });
  }

  activateCycle(cycle: HrPerformanceCycle): void {
    if (!this.canManage || !cycle.performanceCycleId) return;
    this.hrService.activatePerformanceCycle(cycle.performanceCycleId).subscribe({
      next: () => {
        this.toast('success', 'Performance cycle activated.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to activate cycle.')
    });
  }

  saveGoal(): void {
    if (!this.canManage) return;
    this.hrService.savePerformanceGoal(this.goalForm).subscribe({
      next: () => {
        this.goalForm = { goalStatus: 'OPEN' };
        this.activeDialog = undefined;
        this.toast('success', 'Goal saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save goal.')
    });
  }

  saveReview(): void {
    if (!this.canReview) return;
    this.hrService.savePerformanceReview(this.reviewForm).subscribe({
      next: () => {
        this.reviewForm = { reviewType: 'MANAGER', reviewStatus: 'DRAFT', calibrationStatus: 'NOT_CALIBRATED' };
        this.activeDialog = undefined;
        this.toast('success', 'Review saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save review.')
    });
  }

  submitReview(review: HrPerformanceReview): void {
    if (!this.canReview || !review.performanceReviewId) return;
    this.hrService.submitPerformanceReview(review.performanceReviewId).subscribe({
      next: () => {
        this.toast('success', 'Review submitted.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to submit review.')
    });
  }

  calibrateReview(review: HrPerformanceReview): void {
    if (!this.canCalibrate || !review.performanceReviewId) return;
    this.hrService.calibratePerformanceReview(review.performanceReviewId, {
      calibrationRating: review.calibrationRating,
      calibrationStatus: review.calibrationStatus || 'CALIBRATED',
      reviewStatus: review.reviewStatus
    }).subscribe({
      next: () => {
        this.toast('success', 'Review calibrated.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to calibrate review.')
    });
  }

  saveSection(): void {
    if (!this.canReview || !this.sectionForm.performanceReviewId) return;
    this.hrService.savePerformanceReviewSection(this.sectionForm.performanceReviewId, this.sectionForm).subscribe({
      next: () => {
        this.sectionForm = { sectionType: 'COMPETENCY' };
        this.toast('success', 'Review section saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save review section.')
    });
  }

  saveRating(): void {
    if (!this.canReview || !this.ratingForm.performanceReviewId) return;
    this.hrService.savePerformanceReviewRating(this.ratingForm.performanceReviewId, this.ratingForm).subscribe({
      next: () => {
        this.ratingForm = { ratingType: 'GOAL' };
        this.toast('success', 'Review rating saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save rating.')
    });
  }

  saveDevelopmentPlan(): void {
    if (!this.canManage) return;
    this.hrService.saveDevelopmentPlan(this.developmentPlanForm).subscribe({
      next: () => {
        this.developmentPlanForm = { planStatus: 'OPEN' };
        this.activeDialog = undefined;
        this.toast('success', 'Development plan saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save development plan.')
    });
  }

  severity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    if (['ACTIVE', 'APPROVED', 'COMPLETED', 'FINALIZED', 'CALIBRATED'].includes(status || '')) return 'success';
    if (['DRAFT', 'NOT_STARTED', 'NOT_CALIBRATED'].includes(status || '')) return 'secondary';
    if (['SUBMITTED', 'IN_PROGRESS', 'OPEN'].includes(status || '')) return 'warn';
    if (['CLOSED', 'CANCELLED'].includes(status || '')) return 'danger';
    return 'info';
  }

  openActionDialog(dialog: NonNullable<HrPerformanceComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrPerformanceComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrPerformanceComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: 'HR Performance', detail });
  }
}
